import { useState } from 'react';

import { toastService } from '@thefarmersfront/kitchen';

import { useAPI, useAsyncHandler } from '~/hooks/common';
import { addIf, formatDate } from '~/utils';

import { API_ENDPOINT, STOCK_KEYWORD_TYPE_FILTER_KEY, TOAST_MESSAGE } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypeGetVendorReleaseParams,
  TypeGetVendorReleaseResponse,
  TypeStockReleaseFilter,
} from '~/types/stock.types';

/**
 * [재고관리] 반출 요청  api 요청 훅
 */
const usePostVendorReleaseSearch = () => {
  const { runAsync } = useAsyncHandler();

  const [responseData, setResponseData] = useState<TypeGetVendorReleaseResponse | null>(null);

  const postVendorReleaseSearch = useAPI<TypeGetVendorReleaseParams, TypeGetVendorReleaseResponse>({
    endpoint: API_ENDPOINT.STOCK.POST_VENDOR_RELEASE_SEARCH,
    method: 'post',
  });

  const requestVendorRelease = async (
    filterState: TypeStockReleaseFilter,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeGetVendorReleaseResponse) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    } = {}
  ) => {
    const { cluster, status, method, expectedDate, createdDate, keywordType, keywordValue, pageSize, currentPage } =
      filterState;
    const keywordFilterKey =
      STOCK_KEYWORD_TYPE_FILTER_KEY[keywordType?.value as keyof typeof STOCK_KEYWORD_TYPE_FILTER_KEY];

    const params: TypeGetVendorReleaseParams = {
      ...addIf(cluster.value, 'clusterCode'),
      ...addIf(status.value, 'status'),
      ...addIf(method.value, 'method'),
      expectedDateFrom: formatDate(expectedDate?.start, 'YYYY-MM-DD') ?? '',
      expectedDateTo: formatDate(expectedDate?.end, 'YYYY-MM-DD') ?? '',
      ...addIf(formatDate(createdDate?.start, 'YYYY-MM-DD'), 'createdDateFrom'),
      ...addIf(formatDate(createdDate?.end, 'YYYY-MM-DD'), 'createdDateTo'),
      ...addIf(keywordFilterKey && keywordValue, keywordFilterKey, keywordValue),
      cursor: currentPage,
      limit: pageSize,
    };

    runAsync(() => postVendorReleaseSearch({ params }), {
      onSuccess: (res) => {
        setResponseData(res);
        onSuccess?.(res);
      },
      onError: (error: TypeErrorResponseData['error']) => {
        toastService.addToast({
          type: 'error',
          message: error.message ?? TOAST_MESSAGE.DEFAULT_ERROR,
        });
        onError?.(error);
      },
    });
  };

  return { requestVendorRelease, responseData };
};

export default usePostVendorReleaseSearch;
